import api from './api';

export const STATUSES = ['open', 'in_progress', 'waiting_user', 'waiting_third_party', 'resolved', 'closed', 'cancelled'];
export const OPEN_STATUSES = ['open', 'in_progress', 'waiting_user', 'waiting_third_party'];
export const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
export const RESOLUTION_CATEGORIES = ['Solved', "Won't Fix", 'Duplicate', 'Cannot Reproduce', 'Out of Scope'];

export const STATUS_META = {
  open:                { label: 'Aperto',           color: 'var(--wfm-info-text)',    bg: 'var(--wfm-info-bg)',    border: 'var(--wfm-info-border)' },
  in_progress:         { label: 'In lavorazione',   color: 'var(--wfm-purple-text)',  bg: 'var(--wfm-purple-bg)',  border: 'var(--wfm-purple-border)' },
  waiting_user:        { label: 'Attendo utente',   color: 'var(--wfm-warning-text)', bg: 'var(--wfm-warning-bg)', border: 'var(--wfm-warning-border)' },
  waiting_third_party: { label: 'Attendo terzi',    color: 'var(--wfm-warning-text)', bg: 'var(--wfm-warning-bg)', border: 'var(--wfm-warning-border)' },
  resolved:            { label: 'Risolto',          color: 'var(--wfm-success-text)', bg: 'var(--wfm-success-bg)', border: 'var(--wfm-success-border)' },
  closed:              { label: 'Chiuso',           color: 'var(--wfm-neutral-text)', bg: 'var(--wfm-neutral-bg)', border: 'var(--wfm-neutral-border)' },
  cancelled:           { label: 'Annullato',        color: 'var(--wfm-neutral-text)', bg: 'var(--wfm-neutral-bg)', border: 'var(--wfm-neutral-border)' },
};

export const PRIORITY_META = {
  low:    { label: 'Bassa',    color: 'var(--wfm-neutral-text)', bg: 'var(--wfm-neutral-bg)', border: 'var(--wfm-neutral-border)' },
  normal: { label: 'Normale',  color: 'var(--wfm-info-text)',    bg: 'var(--wfm-info-bg)',    border: 'var(--wfm-info-border)' },
  high:   { label: 'Alta',     color: 'var(--wfm-warning-text)', bg: 'var(--wfm-warning-bg)', border: 'var(--wfm-warning-border)' },
  urgent: { label: 'Urgente',  color: 'var(--wfm-error-text)',   bg: 'var(--wfm-error-bg)',   border: 'var(--wfm-error-border)' },
};

const userCache = new Map();

export function clearHelpdeskUserCache() {
  userCache.clear();
}

export async function fetchUsersByIds(ids) {
  const missing = (ids || []).filter((id) => id && !userCache.has(id));
  if (missing.length > 0) {
    try {
      const resp = await api.post('/helpdesk/users/by-ids', { ids: missing });
      for (const u of resp.data || []) userCache.set(u.id, u);
    } catch (_) {}
  }
  const out = {};
  for (const id of ids || []) {
    if (id && userCache.has(id)) out[id] = userCache.get(id);
  }
  return out;
}

export async function searchUsers(q) {
  if (!q || q.trim().length < 2) return [];
  const resp = await api.get(`/helpdesk/users/search?q=${encodeURIComponent(q)}`);
  for (const u of resp.data || []) userCache.set(u.id, u);
  return resp.data || [];
}

export async function listTickets(params = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  return api.get(`/helpdesk/tickets${qs.toString() ? `?${qs}` : ''}`);
}

export async function getTicket(idOrKey) {
  return api.get(`/helpdesk/tickets/${idOrKey}`);
}

export async function createTicket(payload) {
  return api.post('/helpdesk/tickets', payload);
}

export async function patchTicket(idOrKey, payload) {
  return api.patch(`/helpdesk/tickets/${idOrKey}`, payload);
}

export async function changeStatus(idOrKey, status) {
  return api.put(`/helpdesk/tickets/${idOrKey}/status`, { status });
}

export async function changeAssignee(idOrKey, assigneeId) {
  return api.put(`/helpdesk/tickets/${idOrKey}/assignee`, { assigneeId });
}

export async function changeWatchers(idOrKey, payload) {
  return api.put(`/helpdesk/tickets/${idOrKey}/watchers`, payload);
}

export async function addComment(idOrKey, body, internal = false) {
  return api.post(`/helpdesk/tickets/${idOrKey}/comments`, { body, internal });
}

export async function reactToComment(commentId, kind) {
  return api.put(`/helpdesk/comments/${commentId}/reactions`, { kind });
}

export async function resolveTicket(idOrKey, payload) {
  return api.post(`/helpdesk/tickets/${idOrKey}/resolve`, payload);
}

export async function reopenTicket(idOrKey, note) {
  return api.post(`/helpdesk/tickets/${idOrKey}/reopen`, { note });
}

export async function deleteTicket(idOrKey) {
  return api.delete(`/helpdesk/tickets/${idOrKey}`);
}

export async function listReminders(idOrKey) {
  return api.get(`/helpdesk/tickets/${idOrKey}/reminders`);
}

export async function createReminder(idOrKey, payload) {
  return api.post(`/helpdesk/tickets/${idOrKey}/reminders`, payload);
}

export async function snoozeReminder(id, minutes) {
  return api.patch(`/helpdesk/reminders/${id}/snooze`, { minutes });
}

export async function cancelReminder(id) {
  return api.delete(`/helpdesk/reminders/${id}`);
}

export async function listTeams() {
  return api.get('/helpdesk/teams');
}

export async function createTeam(payload) {
  return api.post('/helpdesk/teams', payload);
}

export async function patchTeam(id, payload) {
  return api.patch(`/helpdesk/teams/${id}`, payload);
}

export async function deleteTeam(id) {
  return api.delete(`/helpdesk/teams/${id}`);
}

export async function listTeamMembers(id) {
  return api.get(`/helpdesk/teams/${id}/members`);
}

export async function getTicketParticipants(idOrKey) {
  return api.get(`/helpdesk/tickets/${idOrKey}/participants`);
}

export async function setTeamMember(id, userId, role) {
  return api.put(`/helpdesk/teams/${id}/members`, { userId, role });
}

export async function removeTeamMember(id, userId) {
  return api.delete(`/helpdesk/teams/${id}/members/${userId}`);
}

export async function listTags() {
  return api.get('/helpdesk/tags');
}

export async function createTagDef(payload) {
  return api.post('/helpdesk/tags', payload);
}

export async function deleteTagDef(id) {
  return api.delete(`/helpdesk/tags/${id}`);
}

export async function listSla() {
  return api.get('/helpdesk/sla');
}

export async function updateSla(priority, payload) {
  return api.put(`/helpdesk/sla/${priority}`, payload);
}

export async function getStats() {
  return api.get('/helpdesk/stats');
}

export async function listAttachments(idOrKey) {
  // Attachments come as part of GET /tickets/:idOrKey
  const resp = await getTicket(idOrKey);
  return resp.data?.attachments || [];
}

export async function getAttachment(idOrKey, attId) {
  return api.get(`/helpdesk/tickets/${idOrKey}/attachments/${attId}`);
}

export async function uploadAttachments(idOrKey, attachments) {
  return api.post(`/helpdesk/tickets/${idOrKey}/attachments`, { attachments });
}

// ─── Matrice delle notifiche ─────────────────────────────────────────────
export async function getNotificationMatrix() {
  const r = await api.get('/helpdesk/settings/notifications');
  return r.data;
}

export async function saveNotificationMatrix(changes) {
  const r = await api.put('/helpdesk/settings/notifications', { changes });
  return r.data;
}
